import React from "react";
import { FormikProps } from "formik";
import { LoginFormInitialValues } from "./LoginFormWrapper";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMPassword from "src/components/UI/atoms/formFields/ATMPassword/ATMPassword";
import { LOGIN_PAGE_NAME, LOGIN_PAGE_TAGLINE } from "src/utils/constants";

type Props = {
  formikProps: FormikProps<LoginFormInitialValues>;
  isInvalidPassword: boolean;
};

const brandName = LOGIN_PAGE_NAME?.trim() || "Success Academy";
const tagline =
  LOGIN_PAGE_TAGLINE?.trim() ||
  "Sign in to manage fees, orders, and day-to-day billing.";

const highlights = [
  {
    title: "Fees & receipts",
    text: "Collections, dues, and receipts in one list.",
  },
  {
    title: "Orders & payments",
    text: "Offline orders, advances, and payment modes together.",
  },
  {
    title: "Daily operations",
    text: "Students, franchise, and drawer work without switching tools.",
  },
];

const LoginForm = ({ formikProps, isInvalidPassword }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  return (
    <div className="login-page min-h-screen bg-white lg:grid lg:grid-cols-2">
      <section className="relative flex min-h-screen flex-col justify-center px-6 py-10 sm:px-12">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 hidden w-1 bg-gradient-to-b from-[#e8b15c] via-[#5a7382] to-[#243642] lg:block"
          aria-hidden
        />

        <div className="login-rise mx-auto w-full max-w-[420px]">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fdf9f6] shadow-[0_8px_24px_rgba(49,66,79,0.12)] ring-1 ring-slate-200">
              <img
                src="/success-academy-icon.png?v=3"
                alt={brandName}
                className="h-10 w-10 object-contain"
              />
            </div>
            <div>
              <p className="text-[15px] font-semibold leading-5 text-slate-900">
                {brandName}
              </p>
              <p className="text-[12px] font-medium tracking-wide text-slate-500">
                Billing console
              </p>
            </div>
          </div>

          <p className="text-[13px] font-semibold uppercase tracking-[0.16em] text-[#4c6848]">
            Welcome back
          </p>
          <h1 className="mt-2 text-[32px] font-semibold leading-tight tracking-tight text-slate-900">
            Sign in
          </h1>
          <p className="mt-2 max-w-sm text-[15px] leading-6 text-slate-500">
            {tagline}
          </p>

          <div className="mt-8 flex flex-col gap-6">
            <ATMTextField
              name="email"
              required
              value={values.email}
              onChange={(e) => setFieldValue("email", e.target.value)}
              label="Email"
              placeholder="you@example.com"
              onBlur={handleBlur}
              autoComplete="email"
            />

            <ATMPassword
              name="password"
              required
              value={values.password}
              onChange={(e) => setFieldValue("password", e.target.value)}
              label="Password"
              placeholder="Enter your password"
              onBlur={handleBlur}
              autoComplete="current-password"
              extraClasses={`login-password ${
                isInvalidPassword ? "shake-horizontal" : ""
              }`}
            />

            <ATMLoadingButton
              isLoading={isSubmitting}
              type="submit"
              className="mt-2 h-12 rounded-xl text-[15px] font-semibold tracking-wide shadow-[0_10px_24px_rgba(49,66,79,0.22)]"
            >
              Login
            </ATMLoadingButton>
          </div>
        </div>
      </section>

      <aside className="relative hidden min-h-screen overflow-hidden bg-[#243642] lg:flex">
        <div
          className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-[#e8b15c]/25 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-28 -right-10 h-[28rem] w-[28rem] rounded-full bg-[#5a7382] blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "26px 26px",
          }}
          aria-hidden
        />

        <div className="relative z-10 flex w-full flex-col justify-center px-12 py-12 xl:px-16">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[12px] font-medium tracking-wide text-white/90 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-[#e8b15c]" />
            {brandName}
          </div>

          <div className="mt-8 max-w-lg">
            <h2 className="text-[42px] font-semibold leading-[1.15] tracking-tight text-white xl:text-[48px]">
              One place for billing and operations.
            </h2>
            <p className="mt-4 max-w-md text-[16px] leading-7 text-white/75">
              Keep fees, orders, receipts, and daily work on a single dashboard
              built for {brandName}.
            </p>

            <ul className="mt-10 grid gap-3">
              {highlights.map((item) => (
                <li
                  key={item.title}
                  className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3.5 backdrop-blur-sm"
                >
                  <p className="text-[15px] font-semibold text-white">
                    {item.title}
                  </p>
                  <p className="mt-0.5 text-[13px] leading-5 text-white/70">
                    {item.text}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default LoginForm;
